import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../services/authService';

const DEFAULT_TAG_PREFS = { city: true, gender: true, occupation: true, interest: true };

function normalizeUser(rawUser) {
  if (!rawUser) return null;

  const interests = rawUser.profile?.interests || rawUser.interests || [];
  const bio = rawUser.profile?.bio ?? rawUser.bio ?? '';
  const socialPreference = rawUser.socialPreference || rawUser.social_preference || 'similar';
  const publicId = rawUser.publicId || rawUser.public_id || '';
  const shrimpId = rawUser.shrimpId || rawUser.shrimp_id || '';
  const shrimpIdUpdatedAt = rawUser.shrimpIdUpdatedAt || rawUser.shrimp_id_updated_at || '';
  const gender = rawUser.gender || rawUser.profile?.gender || '';
  const city = rawUser.city || rawUser.profile?.city || '';
  const occupation = rawUser.occupation || rawUser.profile?.occupation || '';
  const avatarId = rawUser.avatarId || rawUser.avatar_id || '';
  const avatarSource = rawUser.avatarSource || rawUser.avatar_source || 'ai';
  const profileModes = {
    bio: rawUser.profileModes?.bio || rawUser.bio_mode || 'auto',
    city: rawUser.profileModes?.city || rawUser.city_mode || 'auto',
    occupation: rawUser.profileModes?.occupation || rawUser.occupation_mode || 'auto',
  };
  const tagPrefs = { ...DEFAULT_TAG_PREFS, ...(rawUser.tagPrefs || {}) };
  const allowMatching =
    rawUser.allowMatching !== undefined
      ? Boolean(rawUser.allowMatching)
      : rawUser.allow_matching !== undefined
        ? Boolean(rawUser.allow_matching)
        : true;
  const guestExpiresAt = rawUser.guestExpiresAt || rawUser.guest_expires_at || null;
  const guestMessageLimit = rawUser.guestMessageLimit || rawUser.guest_message_limit || 0;
  const guestMessageCount = rawUser.guestMessageCount || rawUser.guest_message_count || 0;

  return {
    ...rawUser,
    interests,
    bio,
    socialPreference,
    publicId,
    shrimpId,
    shrimpIdUpdatedAt,
    gender,
    city,
    occupation,
    avatarId,
    avatarSource,
    profileModes,
    tagPrefs,
    allowMatching,
    isGuest: Boolean(rawUser.isGuest || rawUser.is_guest),
    guestExpiresAt,
    guestMessageLimit,
    guestMessageCount,
    profile: {
      bio,
      interests,
      city,
      occupation,
      gender,
    },
  };
}

function extractUserPayload(payload) {
  if (!payload) return null;
  if (payload.data?.token && payload.data?.data) return payload.data.data;
  if (payload.token && payload.data) return payload.data;
  return payload.data || payload;
}

const storedUser = normalizeUser(JSON.parse(localStorage.getItem('user')));

const initialState = {
  user: storedUser,
  isAuthenticated: Boolean(storedUser),
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: ''
};

// Register user
export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Registration failed');
    }
  }
);

// Login user
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const guestLogin = createAsyncThunk(
  'auth/guestLogin',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.guestLogin();
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Guest login failed');
    }
  }
);

// Get user profile
export const getProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getProfile();
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to get profile');
    }
  }
);

// Update user profile
export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.updateProfile(userData);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update profile');
    }
  }
);

// Clear AI profile (wipes 虾问 history + personality)
export const clearAiProfile = createAsyncThunk(
  'auth/clearAiProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.clearAiProfile();
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to clear AI profile');
    }
  }
);

// Logout user
export const logout = createAsyncThunk('auth/logout', async () => {
  await authService.logout();
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = '';
    }
  },
  extraReducers: (builder) => {
    builder
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isAuthenticated = true;
        state.user = normalizeUser(extractUserPayload(action.payload));
        state.message = action.payload.message || 'Registration successful';
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
        state.user = null;
        state.isAuthenticated = false;
      })
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = '';
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isAuthenticated = true;
        state.user = normalizeUser(extractUserPayload(action.payload));
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
        state.user = null;
        state.isAuthenticated = false;
      })
      .addCase(guestLogin.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = '';
      })
      .addCase(guestLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isAuthenticated = true;
        state.user = normalizeUser(extractUserPayload(action.payload));
      })
      .addCase(guestLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
        state.user = null;
        state.isAuthenticated = false;
      })
      // Get Profile
      .addCase(getProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = normalizeUser(action.payload);
        localStorage.setItem('user', JSON.stringify(state.user));
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = normalizeUser(action.payload);
        localStorage.setItem('user', JSON.stringify(state.user));
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Clear AI profile
      .addCase(clearAiProfile.fulfilled, (state, action) => {
        state.user = normalizeUser(action.payload);
        localStorage.setItem('user', JSON.stringify(state.user));
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
      });
  }
});

export const { reset } = authSlice.actions;
export default authSlice.reducer;
